import type { VisualDesignAnalysis } from '@modules/shared-types';
import { createDefaultFlowDocument } from '@modules/themes/factories';
import { defaultThemeV2Mapper } from '@modules/themes/mappers/DefaultThemeV2Mapper';
import {
  DEFAULT_ACTION_BAR,
  DEFAULT_BACKDROP,
  DEFAULT_HEADER_BAR,
  DEFAULT_THEME_V2_COLORS,
  DEFAULT_THEME_V2_FONTS,
  DEFAULT_THEME_V2_LAYOUT_SETTINGS,
  DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS,
  type CreateThemeV2Input,
  type GeneratedThemeV2Metadata,
  type ThemeV2,
} from '@modules/themes/types/themesV2.types';
import {
  applyThemeV2ProjectionPatch,
  type ThemeV2ProjectionPatch,
} from './projectVisualAnalysisToThemeV2';
import { normalizeThemeV2LayoutSettingsForDraft } from './normalizeThemeV2LayoutSettings';
import { stabilizeGeneratedThemeV2Theme } from './stabilizeGeneratedThemeV2Theme';

export interface CreateThemeV2DraftFromProjectionOptions {
  name: string;
  projectionPatch: ThemeV2ProjectionPatch;
  baseTheme?: ThemeV2 | null;
  analysis?: VisualDesignAnalysis;
  sourceImageUrls?: string[];
  sourceAssetIds?: string[];
  provider?: string;
  model?: string;
  warnings?: string[];
  generatedAt?: string;
  generatorVersion?: string;
  /** Defaults to true. Disable only for raw extractor/eval fixtures. */
  normalizeLayoutSettings?: boolean;
}

function cloneJson<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function cloneRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') return undefined;
  return cloneJson(value as Record<string, unknown>);
}

function hasObjectValue(value: unknown): boolean {
  return Boolean(value && typeof value === 'object' && Object.keys(value as Record<string, unknown>).length > 0);
}

function createBaseTheme(name: string, baseTheme: ThemeV2 | null | undefined, now: string): ThemeV2 {
  if (baseTheme) {
    return {
      ...cloneJson(baseTheme),
      id: baseTheme.id || 'theme_v2_image_draft',
      name,
      createdAt: now,
      updatedAt: now,
    };
  }

  return {
    id: 'theme_v2_image_draft',
    name,
    colors: cloneJson(DEFAULT_THEME_V2_COLORS),
    fonts: cloneJson(DEFAULT_THEME_V2_FONTS),
    optionsListSettings: cloneJson(DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS),
    layoutSettings: cloneJson(DEFAULT_THEME_V2_LAYOUT_SETTINGS),
    background: cloneJson(DEFAULT_BACKDROP),
    headerBar: cloneJson(DEFAULT_HEADER_BAR),
    actionBar: cloneJson(DEFAULT_ACTION_BAR),
    flowDocument: createDefaultFlowDocument(),
    createdAt: now,
    updatedAt: now,
  };
}

function collectConfidenceValues(value: unknown, target: number[]): void {
  if (!value || typeof value !== 'object') return;
  if ('confidence' in value && typeof (value as { confidence?: unknown }).confidence === 'number') {
    target.push((value as { confidence: number }).confidence);
  }
  for (const child of Object.values(value as Record<string, unknown>)) {
    collectConfidenceValues(child, target);
  }
}

function averageAnalysisConfidence(analysis: VisualDesignAnalysis | undefined): number | undefined {
  if (!analysis) return undefined;
  const values: number[] = [];
  collectConfidenceValues(analysis.tokens, values);
  if (values.length === 0) return undefined;
  const total = values.reduce((sum, value) => sum + value, 0);
  return Math.round((total / values.length) * 100) / 100;
}

function shouldRemoveSecondaryAction(
  options: CreateThemeV2DraftFromProjectionOptions,
  patch: ThemeV2ProjectionPatch
): boolean {
  const actionBarGeometry = options.analysis?.tokens.geometry?.actionBar;
  const hasPrimaryEvidence = Boolean(
    actionBarGeometry?.primaryButton ||
    patch.actionBar?.primary ||
    patch.layoutSettings?.actionBar?.primaryButton
  );
  const hasSecondaryEvidence = Boolean(
    actionBarGeometry?.secondaryButton ||
    patch.actionBar?.secondary ||
    patch.layoutSettings?.actionBar?.secondaryButton
  );

  return Boolean(options.analysis && hasPrimaryEvidence && !hasSecondaryEvidence);
}

function createGeneratedMetadata(
  options: CreateThemeV2DraftFromProjectionOptions,
  existingMetadata: GeneratedThemeV2Metadata | undefined,
  generatedAt: string
): GeneratedThemeV2Metadata {
  const warningSet = new Set<string>([
    ...(existingMetadata?.warnings ?? []),
    ...(options.warnings ?? []),
  ].filter(Boolean));

  return {
    ...existingMetadata,
    sourceKind: 'image-analysis',
    analysisId: options.analysis?.id ?? existingMetadata?.analysisId,
    generatedAt,
    generatorVersion: options.generatorVersion ?? existingMetadata?.generatorVersion ?? 'theme-v2-image-token-v1',
    provider: options.provider ?? existingMetadata?.provider,
    model: options.model ?? options.analysis?.model ?? existingMetadata?.model,
    confidence: averageAnalysisConfidence(options.analysis) ?? existingMetadata?.confidence,
    sourceImageUrls: options.sourceImageUrls?.length ? options.sourceImageUrls : existingMetadata?.sourceImageUrls,
    sourceAssetIds: options.sourceAssetIds?.length ? options.sourceAssetIds : existingMetadata?.sourceAssetIds,
    warnings: warningSet.size > 0 ? Array.from(warningSet) : undefined,
    analysisSummary: options.analysis?.summary ?? existingMetadata?.analysisSummary,
    screenInventoryCount: options.analysis?.screenInventory?.length ?? existingMetadata?.screenInventoryCount,
    evidenceCount: options.analysis?.evidence?.length ?? existingMetadata?.evidenceCount,
    measurementBasis: hasObjectValue(options.analysis?.measurementBasis)
      ? cloneRecord(options.analysis?.measurementBasis)
      : existingMetadata?.measurementBasis,
    projectionPatch: hasObjectValue(options.projectionPatch)
      ? cloneRecord(options.projectionPatch)
      : existingMetadata?.projectionPatch,
  };
}

/**
 * Build a non-persisted Theme V2 draft from an image-analysis projection.
 *
 * The helper keeps draft creation deterministic: merge projection into a base
 * theme, re-run the existing Theme V2 mapper, and strip generated ids/timestamps
 * before callers persist the draft explicitly.
 */
export function createThemeV2DraftFromProjection(
  options: CreateThemeV2DraftFromProjectionOptions
): CreateThemeV2Input {
  const generatedAt = options.generatedAt ?? new Date().toISOString();
  const baseTheme = createBaseTheme(options.name, options.baseTheme, generatedAt);
  const normalized = options.normalizeLayoutSettings === false
    ? { patch: options.projectionPatch, warnings: [] }
    : normalizeThemeV2LayoutSettingsForDraft(options.projectionPatch, {
      analysis: options.analysis,
    });
  const inferenceWarnings: string[] = [];
  if (shouldRemoveSecondaryAction(options, normalized.patch) && baseTheme.actionBar?.secondary) {
    delete baseTheme.actionBar.secondary;
    inferenceWarnings.push('Removed secondary action because the reference images exposed a primary footer button but no secondary footer button.');
  }
  const patchedTheme = applyThemeV2ProjectionPatch(baseTheme, normalized.patch);
  const stabilized = options.normalizeLayoutSettings === false
    ? { theme: patchedTheme, warnings: [] }
    : stabilizeGeneratedThemeV2Theme(patchedTheme);
  const basicScreen = defaultThemeV2Mapper.applyToBasicScreen(stabilized.theme);
  const themed = {
    ...stabilized.theme,
    ...basicScreen,
  };
  const metadata = createGeneratedMetadata({
    ...options,
    warnings: [
      ...(options.warnings ?? []),
      ...normalized.warnings,
      ...stabilized.warnings,
      ...inferenceWarnings,
    ],
  }, themed.generatedMetadata, generatedAt);
  const flowDocument = defaultThemeV2Mapper.applyToFlowDocument(
    themed,
    themed.flowDocument || createDefaultFlowDocument()
  );

  const cleanTheme = cloneJson({
    ...themed,
    name: options.name,
    flowDocument,
    generatedMetadata: metadata,
  });
  const {
    id: _id,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...createInput
  } = cleanTheme;

  return createInput;
}
